import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory, Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ArrowBack from '@material-ui/icons/ArrowBack';
import IconButton from '@material-ui/core/IconButton';
import {
  clearCottage,
  fetchCottageRequest,
  updateCottageRequest,
  cottageVerification,
  changeCottagePristine,
  fetchCottagesTagsRequest,
} from 'actions/cottage';
import * as cottageSelectors from 'selectors/cottage';
import { tagsSelector } from 'selectors/cottages';
import * as userSelectors from 'selectors/user';
import Errors from 'components/shared/Errors';
import { isStrict as isStrictSelector } from 'selectors/user';
import { showSnackbar } from 'actions';
import PATHS from 'constants/paths';
import Main from 'components/templates/Main';
import moment from 'logic/moment';
import EditComponent from '../components/CottageEdit';

const getError = (error) => {
  if (error && error.status === 400 && error.message === 'User does not own room') {
    return {
      status: 400,
      message: (
        <FormattedMessage
          id="error.notAllowedError"
          defaultMessage="You are not allowed to view this page,
            this can happen if you logged in with another e-mail
            address than the one linked to this cottage.
            If that is the case log out and login with the e-mail
            linked to this ad to display this page."
        />
      ),
    };
  }

  return error;
};

const CottageEdit = () => {
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down('xs'));
  const dispatch = useDispatch();
  const { dialog, cottageId } = useParams();
  const history = useHistory();
  const tags = useSelector(tagsSelector);
  const pristine = useSelector(cottageSelectors.pristineSelector);
  const cottage = useSelector(cottageSelectors.data);
  const user = useSelector(userSelectors.userData);
  const error = useSelector(cottageSelectors.error);
  const isLoading = useSelector(cottageSelectors.isFetching);
  const isStrict = useSelector(isStrictSelector);
  const success = useSelector(cottageSelectors.updateSuccess);
  const updatingDialogs = useSelector(cottageSelectors.updatingDialogs);

  React.useEffect(() => {
    if (!isLoading) {
      if (!isStrict) {
        history.replace(`${PATHS.users.profile}?next=${PATHS.cottages.create}`);
      } else if (!cottage || cottage?.id !== parseInt(cottageId, 10)) {
        dispatch(fetchCottageRequest({ cottageId }));
      }
    }
  }, [isStrict]);

  React.useEffect(() => {
    if (!tags || !tags.length) {
      dispatch(fetchCottagesTagsRequest());
    }
  }, []);

  React.useEffect(() => {
    if (!pristine) {
      dispatch(changeCottagePristine(true));
    }

    return () => {
      // clear the cottage only if we are moving away to another section
      const within = history.location.pathname.includes(
        PATHS.cottages.edit.replace(':cottageId', cottageId),
      );

      if (!within) {
        dispatch(clearCottage());
      }
    };
  }, []);

  const onClose = () => {
    history.push(PATHS.cottages.edit.replace(':cottageId', cottageId));
  };

  React.useEffect(() => {
    // redirects to the edit view without any dialog opened
    // the dialog check in the IF makes sure the redirection happens only once
    // since success is re-evaluated when the component mounts again after the redirect
    if (success && success.code === 'informationSaved' && dialog && !pristine) {
      dispatch(
        showSnackbar({
          severity: 'success',
          message: {
            id: 'success.informationSaved',
            defaultMessage: 'Your information has been saved',
          },
        }),
      );

      // STU-258: a calendar save that kept paid bookings booked must keep the
      // dialog open so the host sees the warning instead of it closing silently.
      // Safe to read `cottage` here despite the [success]-only deps: the success
      // reducer sets `data` and `success` in one action, so this render already
      // has the fresh cottage. Adding `cottage` to deps would wrongly re-fire on
      // unrelated refetches.
      const keptBooked = (cottage && cottage.calendar_kept_booked) || [];
      if (!(dialog === 'calendar' && keptBooked.length > 0)) {
        onClose();
      }
    }
  }, [success]);

  React.useEffect(() => {
    if (error && dialog === 'verify' && !pristine) {
      onClose();
    }
  }, [error]);

  const onSubmit = (values) => {
    dispatch(changeCottagePristine(false));
    dispatch(
      updateCottageRequest({
        cottageId,
        dialog,
        values: {
          ...values,
        },
      }),
    );
  };

  const onSendForReview = () => {
    dispatch(changeCottagePristine(false));
    dispatch(
      updateCottageRequest({
        cottageId,
        dialog: 'onSendForReview',
        values: {
          asked_for_review_at: moment().toISOString(),
        },
      }),
    );
    dispatch(
      showSnackbar({
        severity: 'success',
        message: {
          id: 'success.informationSaved',
          defaultMessage: 'Your information has been saved',
        },
      }),
    );
  };

  const onVerify = () => {
    dispatch(changeCottagePristine(false));
    dispatch(
      cottageVerification({
        cottageId,
        dialog: 'verify',
      }),
    );
  };

  return (
    <Main
      title={
        <div>
          <IconButton color="inherit" component={Link} to="/cottages">
            <ArrowBack />
          </IconButton>
          <FormattedMessage
            id="cottages.editAd"
            defaultMessage="Edit ad #{id}"
            values={{
              id: cottage.id,
            }}
          />
          {cottage?.title_sv && !fullscreen ? <> - "{cottage?.title_sv}"</> : ''}
        </div>
      }
    >
      <EditComponent
        onSubmit={onSubmit}
        onClose={onClose}
        isLoading={isLoading}
        dialog={dialog}
        cottage={cottage}
        user={user}
        onSendForReview={onSendForReview}
        updatingDialogs={updatingDialogs}
        onVerify={onVerify}
      />
      <Errors errors={getError(error)} />
    </Main>
  );
};

export default CottageEdit;
